import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LearnSection from "../components/LearnSection";
import SafetySection from "../components/SafetySection";
import SupportSection from "../components/SupportSection";
import FeedbackCarousel from "../components/FeedbackCarousel";
import LoadingAnimation from "../components/LoadingAnimation";
import MatchSwipeCard from "../components/MatchSwipeCard";
import { useCleanupOnUnload } from "../hooks/useCleanupOnUnload";

function Landing() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Clear localStorage when tab is closed
  useCleanupOnUnload();

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <LoadingAnimation isVisible={isLoading} />
      {/* Use consistent Navbar */}
      <Navbar />

      {/* Hero Section */}
      <main className="pt-11 relative overflow-hidden">
        {/* Bottom-Left Corner Wave Background */}
        <div
          className="hidden sm:block lg:block absolute top-70 left-[-100px] sm:left-[-120px] md:left-[-140px] lg:left-[-140px] w-[40%] sm:w-[36%] md:w-[32%] lg:w-[24%] h-[6%] sm:h-[7%] md:h-[8%] lg:h-[8%] z-0 pointer-events-none rotate-[65deg]"
          style={{ top: "calc(25rem + 40px)" }}
        >
          <svg
            viewBox="0 0 800 800"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            className="w-full h-full filter drop-shadow-lg"
          >
            <path
              d="M0,300 Q100,200 240,240 Q380,280 520,250 Q660,210 800,260 L800,800 L0,800 Z"
              fill="#9333EA"
              opacity="0.8"
            />
            <path
              d="M0,400 Q120,310 280,360 Q450,420 600,370 Q720,330 800,370 L800,800 L0,800 Z"
              fill="#C084FC"
              opacity="0.75"
            />
            <path
              d="M0,500 Q150,420 320,470 Q500,530 680,480 L800,800 L0,800 Z"
              fill="#DDD6FE"
              opacity="0.6"
            />
          </svg>
        </div>

        {/* Top-Right Corner Wave Background */}
        <div className="hidden sm:block absolute top-[-10px] right-[-100px] w-[40%] sm:w-[44%] md:w-[44%] lg:w-[24%] h-[4%] sm:h-[5%] md:h-[6%] lg:h-[8%] z-0 pointer-events-none rotate-45">
          <svg
            viewBox="0 0 800 800"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            className="w-full h-full filter drop-shadow-lg transform rotate-180"
          >
            <path
              d="M0,300 Q100,200 240,240 Q380,280 520,250 Q660,210 800,260 L800,800 L0,800 Z"
              fill="#9333EA"
              opacity="0.8"
            />
            <path
              d="M0,400 Q120,310 280,360 Q450,420 600,370 Q720,330 800,370 L800,800 L0,800 Z"
              fill="#C084FC"
              opacity="0.75"
            />
            <path
              d="M0,500 Q150,420 320,470 Q500,530 680,480 L800,800 L0,800 Z"
              fill="#DDD6FE"
              opacity="0.6"
            />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="text-center md:text-left space-y-6">
              <h1 className="heading-primary text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-600 bg-clip-text text-transparent">
                  Start something
                </span>
                <br />
                <span className="text-gray-900">epic.</span>
              </h1>
              <p className="text-lg sm:text-xl text-subtle max-w-lg mx-auto md:mx-0">
                Meet amazing people nearby. Swipe, match, and chat with
                like-minded singles ready for real connections.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <button
                  onClick={() => {
                    setIsLoading(true);
                    setTimeout(() => navigate("/signup"), 2000);
                  }}
                  className="btn-primary px-8 py-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white text-lg font-semibold hover:shadow-pink-500/50"
                >
                  Create account
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="btn-secondary px-8 py-4 rounded-full border-2 border-gray-300 text-gray-700 text-lg font-semibold hover:border-gray-400 hover:bg-gray-50"
                >
                  Sign in
                </button>
              </div>
              <div className="flex items-center gap-8 justify-center md:justify-start text-sm text-subtle">
                <div>
                  <span className="block text-2xl font-bold text-gray-900">
                    50M+
                  </span>
                  <span>Downloads</span>
                </div>
                <div>
                  <span className="block text-2xl font-bold text-gray-900">
                    4.8★
                  </span>
                  <span>Rating</span>
                </div>
                <div>
                  <span className="block text-2xl font-bold text-gray-900">
                    1M+
                  </span>
                  <span>Matches daily</span>
                </div>
              </div>
            </div>

            {/* Right: Visual Stack with Swipe Card */}
            <div className="relative hidden md:block">
              <MatchSwipeCard />
            </div>
          </div>
        </div>
        {/* Features Section */}
        <section className="section-neutral py-12 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="heading-primary text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Why Vibely?
              </h2>
              <p className="text-lg text-subtle">
                The smarter way to meet new people
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="card-hover text-center p-6 rounded-3xl bg-white">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">⚡</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Instant Matches
                </h3>
                <p className="text-gray-600">
                  Get matched with people who like you back instantly
                </p>
              </div>

              <div className="card-hover text-center p-6 rounded-3xl bg-white">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🔒</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Safe & Secure
                </h3>
                <p className="text-gray-600">
                  Your privacy and safety are our top priority
                </p>
              </div>

              <div className="card-hover text-center p-6 rounded-3xl bg-white">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">💬</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Real Conversations
                </h3>
                <p className="text-gray-600">
                  Chat with verified users for genuine connections
                </p>
              </div>
            </div>
          </div>
        </section>
        <LearnSection />
        <SafetySection />
        <SupportSection />
        <FeedbackCarousel />
        {/* CTA Section */}
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-600 py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to meet someone amazing?
            </h2>
            <p className="text-lg sm:text-xl text-white/90 mb-8">
              Join millions finding love on Vibely
            </p>
            <button
              onClick={() => {
                setIsLoading(true);
                setTimeout(() => navigate("/signup"), 2000);
              }}
              className="btn-primary px-8 py-4 rounded-full bg-white text-purple-600 text-lg font-semibold hover:shadow-white/50"
            >
              Get Started - It's Free!
            </button>
          </div>
        </div>
      </main>

      {/* Reusable Footer Component */}
      <Footer />
    </div>
  );
}

export default Landing;
